import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import DialogShell from '../../tasks/components/DialogShell';
import { attendanceApi, ImportResult } from '../payroll.api';

const SAMPLE = `employee_code,date,in_time,out_time
SDLLP-03,2026-10-01,09:28,18:41
SDLLP-05,01-10-2026,09:45,18:30`;

/** Upload the biometric machine's export; one row per employee per day. */
export default function ImportCsvDialog({ open, onClose, onImported }: { open: boolean; onClose: () => void; onImported: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setResult(null);
    }
  }, [open]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await attendanceApi.importCsv(file);
      setResult(r);
      toast.success(`${r.imported} day${r.imported === 1 ? '' : 's'} imported.`);
      onImported();
    } catch (e) {
      toast.error(logError({ message: 'Import failed', error: e, code: 'ATTENDANCE_IMPORT_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell
      open={open}
      title='IMPORT BIOMETRIC ATTENDANCE'
      onClose={onClose}
      onSubmit={result ? onClose : upload}
      submitLabel={result ? 'Done' : 'Import'}
      busy={busy}
      disabled={!result && !file}
      maxWidth='md'
    >
      <Typography variant='body2'>
        A CSV file with a header row and these columns. Dates may be <code>yyyy-MM-dd</code> or <code>dd-MM-yyyy</code>; times are 24-hour{' '}
        <code>HH:mm</code>. A day already in the register is updated; leave the out time blank if there was no out-punch.
      </Typography>
      <Box component='pre' sx={{ m: 0, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontSize: 13, overflowX: 'auto' }}>
        {SAMPLE}
      </Box>
      {!result && (
        <Button variant='outlined' component='label' sx={{ alignSelf: 'flex-start' }}>
          {file ? file.name : 'Choose CSV file'}
          <input hidden type='file' accept='.csv,text/csv' onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Button>
      )}
      {result && (
        <>
          <Alert severity={result.errors.length ? 'warning' : 'success'}>
            Imported {result.imported}, skipped {result.skipped}
            {result.errors.length ? `, ${result.errors.length} problem${result.errors.length === 1 ? '' : 's'}` : ''}.
          </Alert>
          {result.errors.length > 0 && (
            <Box component='ul' sx={{ m: 0, pl: 3, maxHeight: 220, overflowY: 'auto' }}>
              {result.errors.map((err, i) => (
                <li key={i}>
                  <Typography variant='body2'>{err}</Typography>
                </li>
              ))}
            </Box>
          )}
        </>
      )}
    </DialogShell>
  );
}
